import React, { useContext, useEffect, useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Spin } from 'antd';
import moment from 'moment';
import { SMSChatContext } from './SMSChatContext';
import { getSMSMessagesService, markSMSReadService } from '../../services/SMSConversation/index.service';
import SMSChatMessageList from './SMSChatMessageList';
import SMSChatMessageFooter from './SMSChatMessageFooter';
import ImageElement from '../../components/UIElements/ImageElement';
import { capitalizeFirstLetter } from '../../utils/common.util';

const PER_PAGE = 20;

const UserAvatar = ({ customer }) => {
    const [imgError, setImgError] = useState(false);
    const { profilePictureUrl, firstName } = customer || {};
    
    useEffect(() => { setImgError(false); }, [profilePictureUrl]);

    if (!imgError && profilePictureUrl) {
        return (
             <img 
                src={profilePictureUrl} 
                alt="User" 
                className="w-100 h-100 rounded-circle object-cover"
                onError={() => setImgError(true)}
            />
        );
    }
    return <span>{firstName ? firstName.charAt(0).toUpperCase() : '#'}</span>;
};

const formatPhoneNumber = (phone) => {
  if (!phone) return '';

  // Remove non-digit chars
  const cleaned = phone.replace(/\D/g, '');

  if (cleaned.length <= 10) {
    // No country code, just format local number
    return formatLocalNumber(cleaned);
  }

  const countryCodeLength = cleaned.length - 10;
  const countryCode = cleaned.slice(0, countryCodeLength);
  const localNumber = cleaned.slice(countryCodeLength);

  return `+${countryCode} ${formatLocalNumber(localNumber)}`;
};

// Helper: format local 10-digit number as 3-3-4
const formatLocalNumber = (num) => {
  if (num.length !== 10) return num;
  return `${num.slice(0, 3)}${num.slice(3, 6)}${num.slice(6)}`;
};


const SMSChatMessageBox = () => {
    const { t } = useTranslation();
    const { 
        selectedConversation, 
        setSelectedConversation,
        messages, 
        setMessages,
        isLoadingMessages,
        setIsLoadingMessages,
        hasMoreMessages, 
        setHasMoreMessages,
        messagesPage,
        setMessagesPage 
    } = useContext(SMSChatContext);

    const messagesEndRef = useRef(null);
    const [isFetchMoreLoading, setIsFetchMoreLoading] = useState(false);

    // Initial Load
    useEffect(() => {
        if (selectedConversation) {
            fetchMessages(true);
            markRead(selectedConversation.id);
        }
    }, [selectedConversation]);

    const markRead = async (id) => {
        try {
            await markSMSReadService(id);
        } catch (error) {
            console.error("Failed to mark read", error);
        }
    };

    const fetchMessages = async (isReset = false) => {
        if (isReset) setIsLoadingMessages(true);
        else setIsFetchMoreLoading(true);

        try {
            const page = isReset ? 1 : messagesPage;
            const offset = (page - 1) * PER_PAGE;
            const response = await getSMSMessagesService(selectedConversation.id, {
                limit: PER_PAGE,
                offset
            });

            if (response?.success) {
                const { rows, count } = response.data;
                // Rows come as [newest, ..., oldest] usually, or we sort them.
                // Assuming backend sort DESC (newest first).
                
                if (isReset) {
                    setMessages(Array.isArray(rows) ? rows : []);
                    setMessagesPage(2);
                    scrollToBottom();
                } else {
                     setMessages((prev) => [...(Array.isArray(prev) ? prev : []), ...(Array.isArray(rows) ? rows : [])]);
                    setMessagesPage((prev) => prev + 1);
                }
                 setHasMoreMessages(offset + rows.length < count);
            }
        } catch (error) {
            console.error(error);
        } finally {
            if (isReset) setIsLoadingMessages(false);
            else setIsFetchMoreLoading(false);
        }
    };

    const scrollToBottom = () => {
         setTimeout(() => {
            if (messagesEndRef.current) {
                messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
            }
         }, 100);
    };

    const getDisplayName = () => {
        if (selectedConversation.Customer) {
            return `${capitalizeFirstLetter(selectedConversation.Customer.firstName)} ${capitalizeFirstLetter(selectedConversation.Customer.lastName)}`;
        }
        return selectedConversation.customerPhone;
    };

    if (!selectedConversation) {
        return (
             <div className="nk-chat-body">
                <div className="noChatData d-flex flex-column align-items-center justify-content-center w-100 h-100">
                  <ImageElement source="chat-img.svg" alt="chat" />
                  {/* <h4></h4> */}
                  <h4>{t('text.chat.smsBoxNoContentPara')}</h4>
                </div>
             </div>
        );
    }

    return (
        <div className="nk-chat-body show-chat">
            {isLoadingMessages ? (
                 <div className="noChatData d-flex flex-column align-items-center justify-content-center w-100 h-100">
                    <Spin />
                  </div>
            ) : (
                <>
                    {/* Header */}
                    <div className="nk-chat-head">
                        <ul className="nk-chat-head-info">
                            <li className="nk-chat-body-close d-lg-none" onClick={() => setSelectedConversation(null)}>
                                <a href="#back" className="btn btn-icon btn-trigger nk-chat-hide">
                                    <em className="icon ni ni-arrow-left"></em>
                                </a>
                            </li>
                            <li
                            className="nk-chat-head-user"
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 12,
                            }}
                            >
                            <div
                                className={`user-avatar ${
                                !selectedConversation.Customer?.profilePictureUrl ? "bg-purple" : ""
                                }`}
                                style={{
                                width: 44,
                                height: 44,
                                borderRadius: "50%",
                                overflow: "hidden",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                                }}
                            >
                                <UserAvatar customer={selectedConversation.Customer} />
                            </div>

                            <div className="user-info" style={{ lineHeight: 1.2 }}>
                                <div
                                className="lead-text"
                                style={{ fontWeight: 600, marginBottom: 3 }}
                                >
                                {getDisplayName()}
                                </div>
                                <div style={{ fontSize: 13, color: '#f47321' }}>
                                    {formatPhoneNumber(selectedConversation.customerPhone)}
                                </div>
                            </div>
                            </li>
                        </ul>
                    </div>

                    {/* Message List */}
                    <SMSChatMessageList 
                        messages={Array.isArray(messages) ? messages : []}
                        hasMore={hasMoreMessages}
                        loadMore={() => fetchMessages(false)}
                        isFetchMoreLoading={isFetchMoreLoading}
                        messagesEndRef={messagesEndRef}
                    />

                    {/* Footer */}
                    <SMSChatMessageFooter scrollToBottom={scrollToBottom} />
                </>
            )}
        </div>
    );
};

export default SMSChatMessageBox;
